/**
 * Turning a proposal into a sentence.
 *
 * The assistant's cards used to be a `<dl>` of the spec's own keys - `plot_type`,
 * `y_left`, `AC1_CONDENSER_SEA_WATER_INLET_TEMP_FROM_AC_CTRL` - which is exactly
 * right if you wrote the schema and unreadable if you did not. These helpers
 * produce the headline a person would say out loud.
 *
 * They are deliberately conservative: a signal's exact id is still shown, under
 * the details line, because the pretty form is for recognition and the id is
 * what you check before applying something.
 */

import { aggInfo, groupLabel, measureLabel } from '../plot/table-config.ts';
import type { ExportSpec, PlotSpec } from '../../api/types.ts';
import { shipLabel } from '../../utils/ships.ts';

/**
 * Tokens that are names, not words: keep them as they were written.
 *
 * Only two rules, because every richer one guessed wrong on real signals - a
 * length cut-off kept `SEA` shouting and still lowered `CTRL`. A unit or a
 * system tag either carries a digit (`AC1`, `PR01`) or is two characters
 * (`SW`, `KW`). Everything else reads correctly in lower case, including the
 * abbreviations: "from ac ctrl" is not wrong, only informal.
 */
function isAcronym(token: string): boolean {
  return token.length <= 2 || /\d/.test(token);
}

/**
 * `AC1_ELECTRIC_POWER_ABSORBED` -> `AC1 electric power absorbed`.
 * `speedOverGroundNDC` -> `speed over ground`.
 */
export function prettyColumn(id: string): string {
  if (!id) return '';
  if (id === 'timestampNDC') return 'time';
  const spaced = id
    // camelCase and PascalCase boundaries, before the underscore split so both
    // spellings arrive here as the same list of tokens.
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/[_\-.]+/g, ' ')
    .trim();
  const tokens = spaced.split(/\s+/).filter(Boolean);
  return tokens
    .map((t) => (isAcronym(t) ? t : t.toLowerCase()))
    .join(' ')
    // The NDC suffix marks the source system, not the signal.
    .replace(/\s*\bNDC\b\s*$/i, '')
    .trim();
}

function list(ids: string[] | undefined, max = 3): string {
  const names = (ids ?? []).filter(Boolean).map(prettyColumn);
  if (!names.length) return '';
  if (names.length <= max) {
    if (names.length === 1) return names[0] as string;
    return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  }
  return `${names.slice(0, max).join(', ')} and ${names.length - max} more`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `2026-08-16T09:07:46` -> `16 Aug 2026`. Null when it is not a date. */
export function prettyDate(iso: string): string | null {
  return day(iso);
}

function day(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * A window in words.
 *
 * Whole months and round day counts ending today are what people actually say,
 * so they are said - "last 30 days", "August 2026" - and everything else falls
 * back to the two dates, which are never wrong.
 */
export function prettyWindow(start?: string | null, end?: string | null, now = new Date()): string {
  if (!start || !end) return '';
  const from = new Date(start);
  const to = new Date(end);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return '';

  const dayMs = 86_400_000;
  const spanDays = Math.round((to.getTime() - from.getTime()) / dayMs);
  const endsToday = Math.abs(now.getTime() - to.getTime()) < 2 * dayMs;
  if (endsToday && [7, 14, 30, 60, 90, 180, 365].includes(spanDays)) {
    return spanDays === 365 ? 'the last year' : `the last ${spanDays} days`;
  }

  // A whole calendar month, named.
  const startsMonth = from.getDate() === 1 && from.getHours() === 0;
  const wholeMonth =
    startsMonth &&
    to.getFullYear() === from.getFullYear() + (from.getMonth() === 11 ? 1 : 0) &&
    to.getMonth() === (from.getMonth() + 1) % 12 &&
    to.getDate() === 1;
  if (wholeMonth) return `${MONTHS[from.getMonth()]} ${from.getFullYear()}`;

  const a = day(start);
  const b = day(end);
  return a && b ? `${a} to ${b}` : '';
}

/** "AIDAbella", "AIDAbella and 2 more ships". */
function subject(shipId: string, compare: string[] | undefined): string {
  const others = (compare ?? []).filter((s) => s && s !== shipId);
  const primary = shipLabel(shipId);
  if (!others.length) return primary;
  if (others.length === 1) return `${primary} and ${shipLabel(others[0] as string)}`;
  return `${primary} and ${others.length} more ships`;
}

/** What the chart shows, without naming the ship or the window. */
function what(spec: PlotSpec): string {
  switch (spec.plot_type) {
    case 'number':
      return spec.number_label || (spec.number_mode === 'formula'
        ? spec.number_expression || 'a calculated number'
        : `${spec.number_agg || 'latest'} ${prettyColumn(spec.number_column || '')}`);
    case 'gauge': {
      const names = (spec.gauge_dials ?? []).map((d) => d.label || (d.mode === 'formula' ? 'a calculation' : d.column || ''));
      return names.length ? `${list(names, 4)} as gauges` : 'gauges';
    }
    case 'sankey': {
      const nodes = spec.sankey_nodes ?? [];
      const name = (n: (typeof nodes)[number]): string => n.label || (n.kind === 'formula' ? 'a calculation' : n.column || '');
      const last = Math.max(0, ...nodes.map((n) => n.col ?? 0));
      const from = nodes.filter((n) => (n.col ?? 0) === 0).map(name);
      const to = nodes.filter((n) => (n.col ?? 0) === last && last > 0).map(name);
      return from.length && to.length ? `how ${list(from, 4)} flow into ${list(to, 4)}` : 'a flow diagram';
    }
    case 'histogram':
      return `how often ${list(spec.hist_columns)} takes each value`;
    case 'correlation':
      return `how ${list(spec.corr_columns, 4)} relate to each other`;
    case 'pie':
      return spec.pie_mode === 'bands'
        ? `how much of the time ${prettyColumn(spec.pie_band_column ?? '')} spent in each band`
        : `how ${list(spec.pie_columns, 4)} split the total`;
    case 'box':
      return spec.box_mode === 'groups'
        ? `how ${prettyColumn(spec.box_column ?? '')} is spread for each ${prettyColumn(spec.box_group_column ?? '')}`
        : `how spread out ${list(spec.box_columns, 4)} are`;
    case 'table': {
      if (spec.table_mode === 'readings') return `the readings of ${list(spec.table_columns, 4)}`;
      // Labels are already words; a bare aggregate names its signal the way
      // every other card does.
      const cols = (spec.table_measures ?? []).map((m) =>
        m.label?.trim() || (m.column ? `${aggInfo(m.agg).label.toLowerCase()} ${prettyColumn(m.column)}` : measureLabel(m).toLowerCase()),
      );
      const shown = cols.length > 3 ? `${cols.slice(0, 3).join(', ')} and ${cols.length - 3} more` : cols.join(', ');
      const by = (spec.table_groups ?? []).map((g) => (g.kind === 'time' ? groupLabel(g).toLowerCase() : prettyColumn(g.column ?? '')));
      return `a table of ${shown}${by.length ? ` by ${by.join(' and ')}` : ''}`;
    }
    case 'map':
      return spec.color_by ? `the track, coloured by ${prettyColumn(spec.color_by)}` : 'the track';
    case 'heatmap':
      return `${prettyColumn(spec.z_column ?? '')} across ${prettyColumn(
        spec.x_column ?? '',
      )} and ${prettyColumn(spec.y_column ?? '')}`;
    case 'scatter': {
      const y = list([...(spec.y_left ?? []), ...(spec.y_right ?? [])]);
      const x = prettyColumn(spec.x_column ?? 'timestampNDC');
      return y && x ? `${y} against ${x}` : y || x;
    }
    default: {
      const y = list([...(spec.y_left ?? []), ...(spec.y_right ?? [])]);
      const x = spec.x_column && spec.x_column !== 'timestampNDC' ? prettyColumn(spec.x_column) : '';
      if (!y) return 'the selected signals';
      return x ? `${y} against ${x}` : `${y} over time`;
    }
  }
}

/**
 * The card's headline: what, for whom, when.
 *
 * "Speed over ground and shaft power for AIDAbella, the last 30 days"
 */
export function plotHeadline(spec: PlotSpec, now = new Date()): string {
  const parts = [what(spec), `for ${subject(spec.ship_id, spec.compare_ship_ids)}`];
  const when = prettyWindow(spec.start_date, spec.end_date, now);
  const head = parts.filter(Boolean).join(' ');
  return when ? `${head}, ${when}` : head;
}

/** The same, for an export proposal. */
export function exportHeadline(spec: ExportSpec, now = new Date()): string {
  const cols = spec.columns?.length ? list(spec.columns) : 'every signal';
  const when = prettyWindow(spec.start_date, spec.end_date, now);
  const format = (spec.format || 'csv').toUpperCase();
  const head = `${cols} for ${shipLabel(spec.ship_id)}`;
  return `${when ? `${head}, ${when}` : head}, as ${format}`;
}
